/**
 * N1 · Competência 2 (Álgebra e funções) · task-model H1.12.K02
 *
 *   "Escrever a expressão algébrica que descreve uma tabela: o termo geral de uma
 *    progressão aritmética ou a lei de uma função afim."
 *
 * O aluno PRODUZ a expressão — não a reconhece entre alternativas. A correção é por
 * equivalência: "3n + 2", "2 + 3n" e "3(n − 1) + 5" estão todas certas, porque dão os
 * mesmos valores. Os erros típicos também são expressões, e a resposta digitada que
 * equivale a um deles recebe o nome dele.
 *
 * Duas variantes:
 *  - SEQUÊNCIA: os cinco primeiros termos, e o aluno escreve aₙ. O erro que dá nome
 *    ao item é esquecer o "− 1": aₙ = a₁ + n·r.
 *  - FUNÇÃO: uma tabela com x de 0 a 4 num contexto (táxi, reservatório, vela), e o
 *    aluno escreve f(x). O valor em x = 0 é o termo independente; a diferença entre
 *    linhas, a taxa.
 */

import { equivalentes, lerExpressao } from '../../core/expressao.ts';
import { formatBR } from '../../core/num.ts';
import type { Rng } from '../../core/rng.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  EXPR_INCLINACAO_ERRADA,
  EXPR_SEM_MENOS_UM,
  EXPR_SEM_TERMO_INICIAL,
  EXPR_SOMOU_POSICAO,
  EXPR_TROCOU_COEFICIENTES,
} from '../../schema/errors.ts';
import type { ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

type Variante = 'sequencia' | 'funcao';

interface ContextoFuncao {
  readonly texto: string;
  readonly x: string;
  readonly y: string;
  /** O que a função dá, e em função de quê — o comando nomeia os dois. */
  readonly grandeza: string;
  readonly emFuncao: string;
  /** Faixas de a e b que fazem sentido no contexto. */
  readonly a: readonly number[];
  readonly b: readonly number[];
}

const FUNCOES: readonly ContextoFuncao[] = [
  {
    texto:
      'Um táxi cobra um valor fixo, a bandeirada, mais um valor por quilômetro rodado. A tabela mostra o preço da corrida, em reais, para alguns percursos.',
    x: 'Distância x (km)',
    y: 'Preço (R$)',
    grandeza: 'o preço da corrida',
    emFuncao: 'da distância x, em quilômetros',
    a: [2, 3, 4],
    b: [4, 5, 6, 7],
  },
  {
    texto:
      'Uma torneira despeja sempre a mesma quantidade de água, por minuto, num tanque que já tinha um pouco de água. A tabela mostra o volume no tanque, em litros.',
    x: 'Tempo x (min)',
    y: 'Volume (L)',
    grandeza: 'o volume de água no tanque',
    emFuncao: 'do tempo x, em minutos',
    a: [5, 6, 8, 9],
    b: [10, 12, 15, 20],
  },
  {
    texto:
      'Uma vela acesa diminui sempre a mesma quantidade de altura por hora. A tabela mostra a altura da vela, em centímetros.',
    x: 'Tempo x (h)',
    y: 'Altura (cm)',
    grandeza: 'a altura da vela',
    emFuncao: 'do tempo x, em horas',
    a: [-2, -3],
    b: [18, 20, 24],
  },
];

export interface Params {
  readonly variante: Variante;
  /** Sequência: a₁ e a razão. Função: b (valor em 0) e a (taxa). */
  readonly inicio: number;
  readonly passo: number;
  readonly contexto: number;
}

function drawParams(rng: Rng): Params {
  if (rng.bool()) {
    // a₁ ≠ r: com a₁ = r, "r·n" já seria a resposta, e um erro sumiria.
    const passo = rng.pick([2, 3, 4, 5, 6, 7]);
    const inicio = rng.pick([1, 2, 3, 4, 5, 6, 7, 8, 9].filter((v) => v !== passo));
    return { variante: 'sequencia', inicio, passo, contexto: 0 };
  }
  const contexto = rng.int(0, FUNCOES.length - 1);
  const c = FUNCOES[contexto] as ContextoFuncao;
  return { variante: 'funcao', inicio: rng.pick(c.b), passo: rng.pick(c.a), contexto };
}

/** "3n + 2", "3n − 4", "−2x + 18": a expressão bem escrita. */
function linear(a: number, b: number, v: string): string {
  const termo = a === 1 ? v : a === -1 ? `−${v}` : `${a < 0 ? '−' : ''}${Math.abs(a)}${v}`;
  if (b === 0) return termo;
  return `${termo} ${b < 0 ? '−' : '+'} ${Math.abs(b)}`;
}

const n0 = (x: number): string => formatBR(x, 0);

interface Montagem {
  readonly prefixo: string;
  readonly variavel: string;
  readonly chave: string;
  readonly valores: readonly [number, number][];
  readonly erros: { expr: string; error: ErrorCode; explanation: RichText }[];
  readonly resolucao: RichText;
}

function montar(p: Params): Montagem {
  if (p.variante === 'sequencia') {
    const { inicio: a1, passo: r } = p;
    const termo = (n: number): number => a1 + (n - 1) * r;
    const chave = linear(r, a1 - r, 'n');
    return {
      prefixo: 'aₙ =',
      variavel: 'n',
      chave,
      valores: [1, 2, 3, 4, 5].map((n) => [n, termo(n)]),
      erros: [
        {
          expr: linear(r, a1, 'n'),
          error: EXPR_SEM_MENOS_UM,
          explanation: [
            txt(
              `Com aₙ = ${linear(r, a1, 'n')}, o primeiro termo daria ${a1 + r}, e não ${a1}: a razão foi somada uma vez a mais.`,
            ),
          ],
        },
        {
          expr: linear(r, 0, 'n'),
          error: EXPR_SEM_TERMO_INICIAL,
          explanation: [
            txt(`Com aₙ = ${linear(r, 0, 'n')}, a sequência começaria em ${r}, e não em ${a1}.`),
          ],
        },
        {
          expr: linear(a1, r, 'n'),
          error: EXPR_TROCOU_COEFICIENTES,
          explanation: [
            txt(
              `Em aₙ = ${linear(a1, r, 'n')}, o ${a1} multiplica n — mas o que se soma a cada termo é ${r}.`,
            ),
          ],
        },
        {
          expr: `n + ${r}`,
          error: EXPR_SOMOU_POSICAO,
          explanation: [
            txt(`Com aₙ = n + ${r}, a sequência cresceria de 1 em 1, e não de ${r} em ${r}.`),
          ],
        },
      ],
      resolucao: [
        txt(
          `De um termo para o seguinte, soma-se sempre ${r}. Do primeiro termo até o de posição n, a razão é somada n − 1 vezes:`,
        ),
        br,
        txt(`aₙ = ${a1} + (n − 1) · ${r} = ${chave}.`),
        br,
        txt(
          `Conferindo: para n = 1, ${r} · 1 ${a1 - r < 0 ? '−' : '+'} ${Math.abs(a1 - r)} = ${a1}; para n = 5, ${r} · 5 ${a1 - r < 0 ? '−' : '+'} ${Math.abs(a1 - r)} = ${termo(5)}.`,
        ),
      ],
    };
  }
  const { inicio: b, passo: a } = p;
  const f = (x: number): number => a * x + b;
  const chave = linear(a, b, 'x');
  return {
    prefixo: 'f(x) =',
    variavel: 'x',
    chave,
    valores: [0, 1, 2, 3, 4].map((x) => [x, f(x)]),
    erros: [
      {
        expr: linear(b, a, 'x'),
        error: EXPR_TROCOU_COEFICIENTES,
        explanation: [
          txt(
            `Em f(x) = ${linear(b, a, 'x')}, o ${n0(b)} multiplica x — mas ${n0(b)} é o valor em x = 0, e a variação por unidade é ${n0(a)}.`,
          ),
        ],
      },
      {
        expr: linear(a, 0, 'x'),
        error: EXPR_SEM_TERMO_INICIAL,
        explanation: [
          txt(
            `Com f(x) = ${linear(a, 0, 'x')}, o valor em x = 0 seria 0, e a tabela diz ${n0(b)}.`,
          ),
        ],
      },
      {
        expr: linear(f(1), b, 'x'),
        error: EXPR_INCLINACAO_ERRADA,
        explanation: [
          txt(
            `${n0(f(1))} é o valor em x = 1, e não o quanto a tabela varia por unidade: ${n0(f(1))} − ${n0(b)} = ${n0(a)}.`,
          ),
        ],
      },
    ],
    resolucao: [
      txt(
        `Em x = 0, o valor é ${n0(b)}: é o termo que não depende de x. De uma linha para a seguinte, o valor ${a > 0 ? 'aumenta' : 'diminui'} ${n0(Math.abs(a))}: é o coeficiente de x.`,
      ),
      br,
      txt(
        `Logo, f(x) = ${chave}. Conferindo: f(4) = ${n0(a)} · 4 ${b < 0 ? '−' : '+'} ${n0(Math.abs(b))} = ${n0(f(4))}.`,
      ),
    ],
  };
}

const AMOSTRAS: readonly number[] = [0, 1, 2, 3, 5, 10];

/**
 * Dois erros que dão a mesma expressão (com a₁ = 1, "trocou os papéis" e "somou à
 * posição" são ambos n + r) não podem ser dois diagnósticos: fica o primeiro. E um
 * erro que coincide com a chave (táxi com a = b) não é erro nesta variante.
 */
function distintos(chave: string, erros: Montagem['erros'], v: string): Montagem['erros'] {
  const vistos = [lerExpressao(chave, v)];
  return erros.filter((e) => {
    const x = lerExpressao(e.expr, v);
    if (vistos.some((y) => equivalentes(x, y, AMOSTRAS))) return false;
    vistos.push(x);
    return true;
  });
}

export const expressaoDaTabela: ItemTemplate<Params, null> = {
  id: templateId('h1-12-K02-expressao'),
  version: 1,
  taskModel: taskModelCode('H1.12.K02'),
  skills: [skillCode('H1.12'), skillCode('H1.7')],
  axis: 'I',
  level: 'N1',
  title: 'Expressão algébrica a partir de uma tabela',
  responseModel: '2PL',
  difficultySources: ['steps.2', 'inverse-problem'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.variante === 'sequencia') fontes.push('steps.3plus');
    if (p.passo < 0) fontes.push('non-round-numbers');
    return fontes;
  },
  build(p) {
    const m = montar(p);
    const seq = p.variante === 'sequencia';
    const c = FUNCOES[p.contexto] as ContextoFuncao;
    const cab = seq ? ['Posição (n)', 'Termo (aₙ)'] : [c.x, c.y];
    const alt = `Tabela com duas colunas, ${cab[0]} e ${cab[1]}: ${m.valores.map(([x, y]) => `${n0(x)} → ${n0(y)}`).join('; ')}.`;
    return {
      stem: seq
        ? [
            txt(
              'Numa sequência, cada termo é obtido somando sempre o mesmo número ao termo anterior. A tabela mostra os cinco primeiros termos.',
            ),
          ]
        : [txt(c.texto)],
      comando: seq
        ? [txt('Escreva uma expressão para o termo de posição n da sequência: '), strong('aₙ = …')]
        : [
            txt(`Escreva a lei da função que dá ${c.grandeza} em função ${c.emFuncao}: `),
            strong('f(x) = …'),
          ],
      stimuli: [
        {
          kind: 'table',
          motion: 'none',
          audio: { need: 'none' },
          id: 'tabela',
          alt,
          headers: cab.map((h) => [txt(h)]),
          rows: m.valores.map(([x, y]) => [[txt(n0(x))], [txt(n0(y))]]),
        },
      ],
      interaction: {
        kind: 'expression-entry',
        variavel: m.variavel,
        prefixo: m.prefixo,
        // O EXEMPLO NÃO PODE SER UMA RESPOSTA POSSÍVEL. Ele mostra o formato — "use n
        // para a variável; por exemplo, …" —, e "2n + 7" caía em cima da chave em 2 de
        // cada 100 variantes: o item entregava a resposta logo abaixo do campo.
        //
        // Os coeficientes abaixo estão fora das faixas sorteadas, e não é por sorte:
        // na sequência o coeficiente é o passo, de 2 a 7, e 9 não entra; na função ele
        // sai de {2, 3, 4, 5, 6, 8, 9, −2, −3}, e 7 não entra. Quem mexer nas faixas é
        // avisado pela auditoria, que cobra que exemplo e chave nunca coincidam.
        exemplo: seq ? '9n + 12' : '7x − 1',
        chave: m.chave,
        amostras: AMOSTRAS,
        erros: distintos(m.chave, m.erros, m.variavel),
      },
      dicas: seq
        ? [
            [txt('Quanto se soma de um termo da tabela para o seguinte?')],
            [
              txt(
                `Soma-se sempre ${p.passo}. Do 1º termo até o termo de posição n, quantas vezes o ${p.passo} é somado?`,
              ),
            ],
            [
              txt(
                'Escreva: termo = (1º termo) + (número de vezes) × (quanto se soma), e confira a sua expressão com n = 1 e n = 2 na tabela.',
              ),
            ],
          ]
        : [
            [txt('Que valor a tabela dá quando x = 0?')],
            [
              txt(
                `Em x = 0, o valor é ${n0(p.inicio)}: é o número que não depende de x. Quanto o valor muda de uma linha para a seguinte?`,
              ),
            ],
            [
              txt(
                'Essa variação é o número que multiplica x. Confira a sua lei com x = 1 e x = 2 na tabela.',
              ),
            ],
          ],
      rationale: [strong('Resolução. '), ...m.resolucao],
      declared: seq ? 'media' : 'media',
    };
  },
};
